import { twMerge } from "tailwind-merge";
import { createContext, useContext } from "react";

const tableContext = createContext({
  dark: false,
  bordered: false,
  striped: false,
  sm: false,
});

function Table({ className, dark, bordered, striped, sm, ...props }) {
  return (
    <tableContext.Provider
      value={{
        dark,
        bordered,
        striped,
        sm,
      }}
    >
      <div className="w-full overflow-x-auto rounded-xl">
        <table
          className={twMerge([
            "w-full text-left border border-gray-200 rounded-xl overflow-hidden bg-white",
            dark && "bg-slate-900 text-white border-slate-700",
            className,
          ])}
          {...props}
        >
          {props.children}
        </table>
      </div>
    </tableContext.Provider>
  );
}

const theadContext = createContext({
  variant: "default",
});

function TableThead({ className, variant = "default", ...props }) {
  return (
    <theadContext.Provider value={{ variant }}>
      <thead
        className={twMerge(
          "bg-gray-100 border-b border-gray-200",
          variant === "dark" && "bg-slate-900 text-white border-slate-700",
          className
        )}
        {...props}
      >
        {props.children}
      </thead>
    </theadContext.Provider>
  );
}

function TableTbody({ className, ...props }) {
  return (
    <tbody className={twMerge("divide-y divide-gray-100", className)} {...props}>
      {props.children}
    </tbody>
  );
}

function TableTr({ className, ...props }) {
  const table = useContext(tableContext);

  return (
    <tr
      className={twMerge(
        table.striped && "odd:bg-gray-50",
        className
      )}
      {...props}
    >
      {props.children}
    </tr>
  );
}

function TableTh({ className, ...props }) {
  const table = useContext(tableContext);

  return (
    <th
      className={twMerge([
        "px-5 py-4 text-sm font-semibold text-gray-700 uppercase tracking-wide whitespace-nowrap",
        table.dark && "text-gray-200",
        table.bordered && "border border-gray-200",
        table.sm && "px-3 py-2 text-xs",
        className,
      ])}
      {...props}
    >
      {props.children}
    </th>
  );
}

function TableTd({ className, ...props }) {
  const table = useContext(tableContext);

  return (
    <td
      className={twMerge([
        "px-5 py-4 text-sm text-gray-700 whitespace-nowrap",
        table.dark && "text-gray-200 border-slate-700",
        table.bordered && "border border-gray-200",
        table.sm && "px-3 py-2 text-xs",
        className,
      ])}
      {...props}
    >
      {props.children}
    </td>
  );
}

// Attach sub components
Table.Thead = TableThead;
Table.Tbody = TableTbody;
Table.Tr = TableTr;
Table.Th = TableTh;
Table.Td = TableTd;

export default Table;
